import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Button } from '../../../components/ui/button'
import { Search, RefreshCw, Eye, DollarSign, Hash, X } from 'lucide-react'
import { API_BASE_URL } from '../../../lib/config'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table'
import { formatISTDate } from '../../../components/lib/dateUtils'

interface UserTokenUsage {
  user_id: string
  email: string
  username: string
  first_name?: string
  last_name?: string
  total_input_tokens: number
  total_output_tokens: number
  total_cost: number
}

interface SessionTokenUsage {
  session_id: string
  session_name: string
  created_at: string
  model_name?: string | null
  total_input_tokens: number
  total_output_tokens: number
  total_cost: number
}

interface ModelTokenUsage {
  model_name: string
  total_input_tokens: number
  total_output_tokens: number
  total_cost: number
}

interface UserModelTokenUsage {
  user_id: string
  email: string
  username: string
  model_name: string
  total_input_tokens: number
  total_output_tokens: number
  total_cost: number
}

export function TokenUsage() {
  const [users, setUsers] = useState<UserTokenUsage[]>([])
  const [models, setModels] = useState<ModelTokenUsage[]>([])
  const [userModels, setUserModels] = useState<UserModelTokenUsage[]>([])
  const [loading, setLoading] = useState(false)
  const [modelsLoading, setModelsLoading] = useState(false)
  const [userModelsLoading, setUserModelsLoading] = useState(false)
  const [modelsError, setModelsError] = useState<string | null>(null)
  const [userModelsError, setUserModelsError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedModel, setSelectedModel] = useState<string>('')
  const [selectedUser, setSelectedUser] = useState<UserTokenUsage | null>(null)
  const [sessions, setSessions] = useState<SessionTokenUsage[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [sessionsDialogOpen, setSessionsDialogOpen] = useState(false)

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (sessionsDialogOpen) {
      // Store original overflow style
      const originalOverflow = document.body.style.overflow
      const originalPaddingRight = document.body.style.paddingRight
      
      // Get scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
      
      // Prevent body scroll
      document.body.style.overflow = 'hidden'
      
      // Add padding to prevent layout shift from scrollbar disappearing
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`
      }
      
      // Restore on cleanup
      return () => {
        document.body.style.overflow = originalOverflow
        document.body.style.paddingRight = originalPaddingRight
      }
    }
  }, [sessionsDialogOpen])

  useEffect(() => {
    void Promise.all([fetchTokenUsage(), fetchModelTokenUsage(), fetchUserModelTokenUsage()])
  }, [])

  const fetchTokenUsage = async () => {
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}admin/token-usage/users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      })
      
      if (response.ok) {
        const data = await response.json()
        console.log('Token usage data:', data)
        setUsers(data.users || [])
      } else {
        console.error('Failed to fetch token usage:', response.status, response.statusText)
        const errorText = await response.text()
        console.error('Error response:', errorText)
      }
    } catch (error) {
      console.error('Error fetching token usage:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchModelTokenUsage = async () => {
    setModelsLoading(true)
    setModelsError(null)
    try {
      const response = await fetch(`${API_BASE_URL}admin/token-usage/models`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      })

      if (response.ok) {
        const data = await response.json()
        console.log('Token usage by model:', data)
        setModels(data.models || [])
      } else {
        console.error('Failed to fetch token usage by model:', response.status, response.statusText)
        const errorText = await response.text()
        console.error('Error response:', errorText)
        setModelsError(`HTTP ${response.status} ${response.statusText}${errorText ? ` — ${errorText}` : ''}`)
      }
    } catch (error) {
      console.error('Error fetching token usage by model:', error)
      setModelsError(String(error))
    } finally {
      setModelsLoading(false)
    }
  }

  const fetchUserModelTokenUsage = async () => {
    setUserModelsLoading(true)
    setUserModelsError(null)
    try {
      const response = await fetch(`${API_BASE_URL}admin/token-usage/users/models`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      })

      if (response.ok) {
        const data = await response.json()
        console.log('Token usage by user and model:', data)
        setUserModels(data.rows || [])
      } else {
        console.error('Failed to fetch token usage by user and model:', response.status, response.statusText)
        const errorText = await response.text()
        console.error('Error response:', errorText)
        setUserModelsError(`HTTP ${response.status} ${response.statusText}${errorText ? ` — ${errorText}` : ''}`)
      }
    } catch (error) {
      console.error('Error fetching token usage by user and model:', error)
      setUserModelsError(String(error))
    } finally {
      setUserModelsLoading(false)
    }
  }

  const fetchUserSessions = async (userId: string) => {
    setSessionsLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}admin/token-usage/user/${userId}/sessions`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      })
      
      if (response.ok) {
        const data = await response.json()
        setSessions(data.sessions || [])
        setSessionsDialogOpen(true)
      }
    } catch (error) {
      console.error('Error fetching user sessions:', error)
    } finally {
      setSessionsLoading(false)
    }
  }

  const handleViewSessions = (user: UserTokenUsage) => {
    setSelectedUser(user)
    fetchUserSessions(user.user_id)
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 6,
      maximumFractionDigits: 6
    }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.first_name && user.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.last_name && user.last_name.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  const availableModels = Array.from(
    new Set((userModels || []).map((r) => (r.model_name || 'Legacy model').trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b))

  // Keep a concrete model selected (no "All models" option)
  useEffect(() => {
    if (!selectedModel && availableModels.length > 0) {
      setSelectedModel(availableModels[0])
    }
    if (selectedModel && availableModels.length > 0 && !availableModels.includes(selectedModel)) {
      setSelectedModel(availableModels[0])
    }
  }, [availableModels, selectedModel])

  const filteredUserModels = userModels.filter(row => {
    const q = searchTerm.toLowerCase()
    const matchesSearch =
      row.email.toLowerCase().includes(q) ||
      row.username.toLowerCase().includes(q) ||
      row.model_name.toLowerCase().includes(q)

    const matchesModel = selectedModel ? row.model_name === selectedModel : true

    return matchesSearch && matchesModel
  })

  const totalInputTokens = users.reduce((sum, user) => sum + user.total_input_tokens, 0)
  const totalOutputTokens = users.reduce((sum, user) => sum + user.total_output_tokens, 0)
  const totalCost = users.reduce((sum, user) => sum + user.total_cost, 0)

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Input Tokens</CardTitle>
            <Hash className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(totalInputTokens)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Output Tokens</CardTitle>
            <Hash className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(totalOutputTokens)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalCost)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Token Usage by User</CardTitle>
          <CardDescription>
            View token usage and costs for all users. Only shows data for messages with token tracking.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Label htmlFor="search">Search Users</Label>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="search"
                  placeholder="Search by email, username, or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-10 pl-10"
                />
              </div>
            </div>
            <div className="flex items-end">
              <Button
                onClick={() => void Promise.all([fetchTokenUsage(), fetchModelTokenUsage(), fetchUserModelTokenUsage()])}
                disabled={loading || modelsLoading || userModelsLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Model-wise Table */}
      <Card>
        <CardHeader>
          <CardTitle>Token Usage by Model</CardTitle>
          <CardDescription>
            Model-wise token usage and cost (derived from each session's locked model).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {modelsError ? (
            <div className="text-sm text-red-600 dark:text-red-400 whitespace-pre-wrap">
              {modelsError}
            </div>
          ) : null}
          {modelsLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading model-wise token usage...
            </div>
          ) : models.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No model-wise token usage data available.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Model</TableHead>
                    <TableHead className="text-right">Input Tokens</TableHead>
                    <TableHead className="text-right">Output Tokens</TableHead>
                    <TableHead className="text-right">Total Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {models.map((m) => (
                    <TableRow key={m.model_name}>
                      <TableCell className="font-medium">{m.model_name}</TableCell>
                      <TableCell className="text-right font-mono">{formatNumber(m.total_input_tokens)}</TableCell>
                      <TableCell className="text-right font-mono">{formatNumber(m.total_output_tokens)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">{formatCurrency(m.total_cost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User + Model Table */}
      <Card>
        <CardHeader>
          <CardTitle>Token Usage by User and Model</CardTitle>
          <CardDescription>
            User-wise breakdown grouped by model. Use the search box above to filter by email/username/model.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="sm:w-80">
              <Label>Model</Label>
              <Select value={selectedModel} onValueChange={setSelectedModel} disabled={availableModels.length === 0}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent>
                  {availableModels.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {userModelsError ? (
            <div className="text-sm text-red-600 dark:text-red-400 whitespace-pre-wrap">
              {userModelsError}
            </div>
          ) : null}
          {userModelsLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading user-wise model breakdown...
            </div>
          ) : filteredUserModels.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm ? 'No rows found matching your search.' : 'No user-wise model breakdown available.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead className="text-right">Input Tokens</TableHead>
                    <TableHead className="text-right">Output Tokens</TableHead>
                    <TableHead className="text-right">Total Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUserModels.map((row) => (
                    <TableRow key={`${row.user_id}:${row.model_name}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{row.username}</p>
                          <p className="text-sm text-muted-foreground">{row.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{row.model_name}</TableCell>
                      <TableCell className="text-right font-mono">{formatNumber(row.total_input_tokens)}</TableCell>
                      <TableCell className="text-right font-mono">{formatNumber(row.total_output_tokens)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">{formatCurrency(row.total_cost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading token usage data...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm ? 'No users found matching your search.' : 'No token usage data available.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead className="text-right">Input Tokens</TableHead>
                    <TableHead className="text-right">Output Tokens</TableHead>
                    <TableHead className="text-right">Total Cost</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.user_id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">
                            {user.first_name && user.last_name
                              ? `${user.first_name} ${user.last_name}`
                              : user.username}
                          </p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatNumber(user.total_input_tokens)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatNumber(user.total_output_tokens)}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold">
                        {formatCurrency(user.total_cost)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewSessions(user)}
                          disabled={sessionsLoading}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Sessions
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sessions Dialog */}
      {sessionsDialogOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-hidden"
          onClick={(e) => {
            // Close modal when clicking backdrop
            if (e.target === e.currentTarget) {
              setSessionsDialogOpen(false)
            }
          }}
        >
          <div 
            className="w-full max-w-6xl h-[63vh] bg-white dark:bg-gray-900 rounded-lg shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Session-wise Token Usage - {selectedUser?.email}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Detailed breakdown of token usage for each session
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSessionsDialogOpen(false)}
                  className="rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            
            {/* Content Area */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {sessionsLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading session data...
                </div>
              ) : sessions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No session data available for this user.
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="relative overflow-x-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-white dark:bg-gray-900 z-10 [&_tr]:border-b">
                        <TableRow>
                          <TableHead className="bg-white dark:bg-gray-900">Session Name</TableHead>
                          <TableHead className="bg-white dark:bg-gray-900">Model</TableHead>
                          <TableHead className="bg-white dark:bg-gray-900">Created At</TableHead>
                          <TableHead className="text-right bg-white dark:bg-gray-900">Input Tokens</TableHead>
                          <TableHead className="text-right bg-white dark:bg-gray-900">Output Tokens</TableHead>
                          <TableHead className="text-right bg-white dark:bg-gray-900">Cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sessions.map((session) => (
                          <TableRow key={session.session_id}>
                            <TableCell className="font-medium">
                              {session.session_name}
                            </TableCell>
                            <TableCell className="font-mono">
                              {session.model_name || 'Legacy model'}
                            </TableCell>
                            <TableCell>
                              {session.created_at
                                ? formatISTDate(new Date(session.created_at))
                                : 'N/A'}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatNumber(session.total_input_tokens)}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatNumber(session.total_output_tokens)}
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold">
                              {formatCurrency(session.total_cost)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  
                  {/* Session Summary */}
                  <div className="border-t pt-4 shrink-0">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Input Tokens</p>
                        <p className="text-lg font-semibold font-mono">
                          {formatNumber(
                            sessions.reduce((sum, s) => sum + s.total_input_tokens, 0)
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Output Tokens</p>
                        <p className="text-lg font-semibold font-mono">
                          {formatNumber(
                            sessions.reduce((sum, s) => sum + s.total_output_tokens, 0)
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Cost</p>
                        <p className="text-lg font-semibold font-mono">
                          {formatCurrency(
                            sessions.reduce((sum, s) => sum + s.total_cost, 0)
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

